/**
 * Utility to track referral codes across page loads and auth redirects
 */

const REFERRAL_STORAGE_KEY = "referral_code";
const REFERRAL_TIMESTAMP_KEY = "referral_timestamp";
const REFERRAL_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

export class ReferralTracker {
  /**
   * Check for referral code in URL and store it in localStorage
   * Should be called on any page that might receive referral traffic
   */
  static captureReferralFromUrl(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const referralCode = urlParams.get("ref");

    if (referralCode) {
      // Store the referral code and timestamp
      localStorage.setItem(REFERRAL_STORAGE_KEY, referralCode);
      localStorage.setItem(REFERRAL_TIMESTAMP_KEY, Date.now().toString());

      // Clean up URL to remove the referral parameter
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("ref");
      window.history.replaceState({}, "", newUrl.toString());

      console.log("🔗 Referral code captured:", referralCode);
    }
  }

  /**
   * Get the stored referral code if it's still valid
   */
  static getStoredReferralCode(): string | null {
    const code = localStorage.getItem(REFERRAL_STORAGE_KEY);
    const timestamp = localStorage.getItem(REFERRAL_TIMESTAMP_KEY);

    if (!code || !timestamp) {
      return null;
    }

    // Check if the referral code has expired
    const storedTime = parseInt(timestamp, 10);
    const now = Date.now();

    if (now - storedTime > REFERRAL_EXPIRY_MS) {
      // Expired, clean up storage
      this.clearStoredReferralCode();
      return null;
    }

    return code;
  }

  /**
   * Clear the stored referral code
   */
  static clearStoredReferralCode(): void {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
    localStorage.removeItem(REFERRAL_TIMESTAMP_KEY);
  }

  /**
   * Check if a user was referred and trigger pro conversion tracking
   * Should be called when a user upgrades to pro
   */
  static async triggerProConversion(userId: string): Promise<void> {
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:8000"
        }/api/referrals/process-pro-conversion`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ user_id: userId }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log("✅ Pro conversion processed for referral system");
        }
      }
    } catch (error) {
      console.error("Error processing pro conversion for referrals:", error);
    }
  }
}
